import { TextField } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import FilledInput from '@material-ui/core/FilledInput';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import LayersIcon from '@material-ui/icons/Layers';
import BookmarksOutlinedIcon from '@material-ui/icons/BookmarksOutlined';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import CloseSharpIcon from '@material-ui/icons/CloseSharp';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import GradeIcon from '@material-ui/icons/Grade';
import HistoryIcon from '@material-ui/icons/History';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import People from '@material-ui/icons/People';
import ShoppingCart from '@material-ui/icons/ShoppingCart';
import SmsOutlinedIcon from '@material-ui/icons/SmsOutlined';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { observer } from 'mobx-react';
import { Instance, types } from 'mobx-state-tree';
import * as React from 'react';
import background from '../assets/background.jpg';
import { LogoIcon } from '../LogoIcon';
import { transl } from '../main/transl';
import { mg } from '../mg/mg';
import { useOmnistore } from '../stores/Omnistore';

const SidebarStore = types
	.model('SidebarStore', {
		_state: types.enumeration(['open', 'half', 'closed']),
	})
	.actions(self => ({
		open() {
			self._state = 'open';
		},
		half() {
			self._state = 'half';
		},
		close() {
			self._state = 'closed';
		},
	}))
	.views(self => ({
		get isOpen() {
			return self._state === 'open';
		},
		get isClosed() {
			return self._state === 'closed';
		},
	}));

type ISidebarStore = Instance<typeof SidebarStore>

export const sidebarStore = SidebarStore.create({_state: 'open'});

const drawerWidth = 240;
const marginTop = 50;

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		display: 'flex',
		height: '100%',
	},
	appBar: {
		zIndex: theme.zIndex.drawer + 1,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		color: '#222222',
		backgroundColor: '#EEEEEE',
	},
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
	drawerPaper: {
		backgroundImage: `url(${background})`,
		backgroundRepeat: 'repeat',
	},
	drawerOpen: {
		width: drawerWidth,
		transition: theme.transitions.create('width', {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	},
	drawerClose: {
		transition: theme.transitions.create('width', {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		overflowX: 'hidden',
		width: theme.spacing(7.1),
		[theme.breakpoints.up('sm')]: {
			width: theme.spacing(9.1),
		},
	},
	toolbar: {
		display: 'flex',
		alignItems: 'left',
		...theme.mixins.toolbar,
		justifyContent: 'space-between',
	},
	closers: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	},
	logo: {
		display: 'block',
		maxWidth: '8em',
		height: '8em',
		margin: 0,
		marginTop: '0.5em',
	},
	content: {
		flex: 1,
		padding: theme.spacing(2),
		marginTop: marginTop,
		height: `calc(100% - ${marginTop}px)`,
	},
	branchSelector: {
		width: '100%',
	},
	supplierSelector: {
		width: '100%',
		'& .MuiInput-input': {
			color: '#FFF',
		},
	},
	supplierPopper: {
		'& li[data-focus="true"]': {
			backgroundColor: '#4CAF50',
		},
	},
	sidebarItem: {
		color: '#EEEEEE',
	},
	roleSelect: {
		margin: theme.spacing(2),
		borderRadius: 20,
		color: theme.palette.common.white,
		backgroundColor: theme.palette.primary.main,
		'&:hover': {
			backgroundColor: theme.palette.primary.main,
		},
		'&.Mui-focused': {
			backgroundColor: theme.palette.primary.main,
		},
		'&.MuiFilledInput-underline:after': {
			border: 'none',
		},
		'& div.MuiSelect-root': {
			position: 'relative',
			left: 10,
			bottom: 5,
		},
	},
	sidebarItemSelected: {
		color: '#FE4068',
		letterSpacing: '1.0px',
		// color: theme.palette.secondary.main, //TODO
		// fontSize: '120%',
		fontWeight: 800,
	},
	sidebarSelect: {
		color: '#EEEEEE',
		backgroundColor: '#EEEEEE20',
	},
	sidebarSubheader: {
		color: '#EEEEEE',
	},
	sidebarIconButton: {
		color: '#EEEEEE',
	},
	sidebarDivider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
		backgroundColor: '#EEEEEE',
	},
}));

interface ISidebarItemProps {
	disabled?: boolean;
	name: string;
	icon: React.ComponentType<SvgIconProps>;
}

interface ISidebarProps {
	store: ISidebarStore;
}

const SidebarItem = observer(function SidebarItem(props: ISidebarItemProps) {
	const classes = useStyles();
	const {disabled, name, icon: ThisIcon} = props;
	const omnistore = useOmnistore();
	const isSelected = name === omnistore.locationStore.page;
	const className = mg.clsn(classes.sidebarItem, isSelected && classes.sidebarItemSelected);
	function menuClick() {
    if( props.name == 'ImpressumPage'){
      window.open( "https://www.octopusorder.com/impressum/", "Impressum")
      return;
    }
		if (!disabled) omnistore.locationStore.setPage(name);
	}

	return <ListItem button
		onClick={menuClick}
		disabled={disabled}
		selected={isSelected}>
		<ListItemIcon><ThisIcon className={className}/></ListItemIcon>
		<ListItemText primary={transl(name)} primaryTypographyProps={{className}}/>
	</ListItem>;
});

const SidebarBranchSelect = observer(function SidebarBranchSelect() {
	const classes = useStyles();
	const omnistore = useOmnistore();
	const repo = omnistore.multirepo.branchRepo;

	return <FormControl variant="filled" className={classes.branchSelector}>
		<Select
			value={omnistore.locationStore.branchId}
			onChange={e => omnistore.locationStore.setBranchId(e.target.value as any)}
			input={<FilledInput className={classes.sidebarSelect} name="branch"/>}>
			{!repo.etag ? null : repo.allAsList.map(e => <MenuItem value={e.id} key={e.id}>{e.name}</MenuItem>)}
		</Select>
	</FormControl>;
});

const SidebarSupplierSelect = observer(function SidebarSupplierSelect() {
	const classes = useStyles();
	const omnistore = useOmnistore();
	// const repo = new FilterRepo(omnistore.multirepo.registeredSupplierRepo, s => s.kind==='ONLINE'); //TODO
	const repo = omnistore.multirepo.registeredSupplierRepo;
	React.useEffect(() => {
		repo.fetch();
	}, []);
	const router = omnistore.locationStore;
	const supplier = repo.byId(router.supplierId);

	return <FormControl variant="filled" className={classes.supplierSelector}>
		<Autocomplete
			classes={{popper: classes.supplierPopper}}
			selectOnFocus blurOnSelect openOnFocus
			value={supplier?.id ?? 0}
			onChange={(e: unknown, value: number|null) => value && router.setSupplierId(value)}
			getOptionLabel={id => !id ? '' : repo.byId(id)?.name ?? '?'}
			// input={<FilledInput className={classes.sidebarSelect} name="branch"/>}
			renderInput={params => <TextField {...params}/>}
			options={repo.allAsList.slice().sort(mg.comparatorOn(e => e.name)).map(e => e.id)}
		/>
	</FormControl>;
});

const StandardSidebarList = observer(function StandardSidebarList() {
	const classes = useStyles();
	const isTester = useOmnistore().reoctoStore.isTester;
	return <List>
		<ListSubheader className={classes.sidebarSubheader} component="div">{transl('general')}</ListSubheader>
		<SidebarItem name="GDashboard" icon={AccountBalanceIcon}/>
		<SidebarItem name="OrderPage" icon={HistoryIcon}/>
		<SidebarItem name="AdsPage" icon={CardGiftcardIcon}/>

		<Divider className={classes.sidebarDivider}/>
		<ListSubheader className={classes.sidebarSubheader} component="div">{transl('location')}</ListSubheader>
		<SidebarBranchSelect/>

		<SidebarItem name="LDashboard" icon={AccountBalanceIcon}/>
		{!isTester ? <SidebarItem name="LTemplates" icon={ShoppingCart}/> : <>
			<SidebarItem name="LTemplates" icon={FormatListBulletedIcon}/>
			<SidebarItem name="SupplierOffers" icon={LocalOfferIcon}/>
			<SidebarItem name="Checkout" icon={ShoppingCart}/>
		</>}
		<SidebarItem name="TeamPage" icon={People}/>
		<SidebarItem name="ImpressumPage" icon={LayersIcon}/>
	</List>;
});

const AdminSidebarList = observer(function AdminSidebarList() {
	const classes = useStyles();
	return <List>
		<SidebarItem name="GDashboard" icon={AccountBalanceIcon}/>
		<SidebarItem name="HarmoPage" icon={AccountBalanceIcon}/>
		<SidebarItem name="BrandPage" icon={AccountBalanceIcon}/>
		<SidebarItem name="DbStatPage" icon={AccountBalanceIcon}/>
		<SidebarItem name="OrderPage" icon={HistoryIcon}/>
		<SidebarItem name="AdmUserPage" icon={AccountBalanceIcon}/>
		<SidebarItem name="AdmSupplierPage" icon={AccountBalanceIcon}/>
		<SidebarItem name="AdmTemplatePage" icon={AccountBalanceIcon}/>

		<Divider className={classes.sidebarDivider}/>
		<ListSubheader className={classes.sidebarSubheader} component="div">{transl('supplier')}</ListSubheader>
		<SidebarSupplierSelect/>

		<SidebarItem name="AdmSupplierItemPage" icon={AccountBalanceIcon}/>
		<SidebarItem name="AdmPretemplatePage" icon={AccountBalanceIcon}/>
		<SidebarItem name="ImpressumPage" icon={LayersIcon}/>
	</List>;
});

const HarmonizerSidebarList = observer(function HarmonizerSidebarList() {
	const classes = useStyles();
	const isTester = useOmnistore().reoctoStore.isTester;
	return <List>
		<ListSubheader className={classes.sidebarSubheader}>{transl('dataManagement')}</ListSubheader>
		<SidebarItem name="HarmoPage" icon={BookmarksOutlinedIcon}/>
		<SidebarItem icon={LocationOnOutlinedIcon} name="OutletPage"/>
		<ListSubheader className={classes.sidebarSubheader}>{transl('adsManagement')}</ListSubheader>
		<SidebarItem name="AdmAdPage" icon={GradeIcon}/>
		<SidebarItem name="SendMessagePage" icon={SmsOutlinedIcon}/>
		<SidebarItem name="AdmCouponPage" icon={LocalAtmIcon}/>
		{!isTester ? null : <>
			<ListSubheader className={classes.sidebarSubheader}>tester-only</ListSubheader>
			<SidebarItem name="BrandPage" icon={AccountBalanceIcon}/>
		</>}
		<SidebarItem name="ImpressumPage" icon={LayersIcon}/>
	</List>;
});

const SidebarList = observer(function SidebarList() {
	const omnistore = useOmnistore();
	switch (omnistore.authStore.role) {
	case 'STANDARD': return <StandardSidebarList/>;
	case 'ADMIN': return <AdminSidebarList/>;
	case 'HARMONIZER': return <HarmonizerSidebarList/>;
	default: return null;
	}
});

export const Sidebar = observer(function Sidebar(props: ISidebarProps) {
	const classes = useStyles();
	const {store} = props;
	const authStore = useOmnistore().authStore;
	if (store.isClosed) return null;
	return <Drawer
		variant="permanent"
		className={mg.clsn(classes.drawer, store.isOpen ? classes.drawerOpen : classes.drawerClose)}
		classes={{
			paper: mg.clsn(classes.drawerPaper, store.isOpen ? classes.drawerOpen : classes.drawerClose),
		}}
		open={store.isOpen}
	>
		<div className={classes.toolbar}>
			<div className={classes.closers}>
				<IconButton className={classes.sidebarIconButton} onClick={store.close}>{<CloseSharpIcon/>}</IconButton>
			</div>
			{!store.isOpen ? null : <LogoIcon className={classes.logo}/>}
			<div className={classes.closers}>
				<IconButton className={classes.sidebarIconButton} onClick={store.half}>{<ChevronLeftIcon/>}</IconButton>
			</div>
		</div>
		{
			authStore.roles.length<=1 ? null : <Select
				variant="filled"
				value={authStore.role}
				onChange={e => authStore.setRole(e.target.value as any)}
				className={classes.roleSelect}
			>
				{authStore.roles.map(s => <MenuItem value={s} key={s}>{s}</MenuItem>)}
			</Select>
		}
		<SidebarList/>
	</Drawer>;
});
